import { STEPS, useRegistration } from "#/components/views/register/RegistrationContext"
import { CarrotErrorCodes } from "#/utils/CarrotErrors"
import { isHttpError, parseErrorBody } from "#/utils/HttpErrors"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { carrotClient } from "#/utils/CarrotClient"
import { Paths } from "#/utils/Paths"
import Helpers from "#/utils/Helpers"
import useUpdateRegisteringMemberActionTracking from "#/services/memberActionTracking/useUpdateRegisteringMemberActionTracking"
import { useTrackExperimentEvent } from "#/components/views/register/hooks/useExperiments"
import { getIsDefaultEmailOptIn } from "#/components/views/register/EmailOptIn"
import { useGetIsExcludedFromMedicalRecordsConsent } from "#/services/user/hooks/useGetIsExcludedFromMedicalRecordsConsent"
import { useCreateUser } from "#/components/views/register/hooks/useCreateUser"
import { useIntl } from "react-intl"
import { RegisteringUser } from "#/lib/carrot-api/types/RegisteringUser"
import { RTX_PARENT_COMPANY_ID } from "#/components/consent/hooks/useIsRtxParentCompany"
import { isPregnancyTrimesterStage, isPostpartumStage } from "#/components/carrot-plans/hooks/useJourneyStageOptions"
import { AboutYou } from "#/types/carrotPlanTypes"

type UseRegistrationNavigation = {
  // STEPS.INTRO
  onCompleteIntro: () => Promise<void>
  // STEPS.CONFIRM_ACCOUNT_EMAIL
  onBackConfirmAccountEmail: () => void
  onCompleteConfirmAccountEmail: () => void
  // STEPS.SET_PASSWORD
  onBackSetPassword: () => void
  onCompleteSetPassword: (password: string) => void
  // STEPS.PERSONAL_EMAIL
  onBackPersonalEmail: () => void
  onCompletePersonalEmail: (email: string | null) => void
  // STEPS.PREFERRED_NAME
  onBackPreferredName: () => void
  onCompletePreferredName: () => void
  // STEPS.ADDRESS
  onBackAddress: () => void
  onCompleteAddress: (address: AboutYou["address"]) => void
  // STEPS.PHONE_NUMBER_COLLECTION
  onBackPhoneNumberCollection: () => void
  onCompletePhoneNumberCollection: () => void
  // STEPS.ACKNOWLEDGE_TERMS
  onBackAcknowledgeTerms: () => void
  onCompleteAcknowledgeTerms: () => void
  // STEPS.HEALTH_CONSENT
  onBackHealthConsent: () => void
  onCompleteHealthConsent: () => void
  // STEPS.EMAIL_OPT_IN
  onBackEmailOptIn: () => void
  onCompleteEmailOptIn: () => void
  // STEPS.RTX_REWARDS_OPT_IN
  onBackRTXRewardsOptIn: () => void
  onCompleteRTXRewardsOptIn: (optedIn: boolean) => void
  // STEPS.WHAT_KIND_OF_JOURNEY
  onBackJourney: () => void
  onCompleteJourney: ({
    selectedJourney,
    somethingElseJourney,
    skipped
  }: {
    selectedJourney: string
    somethingElseJourney: string
    skipped: boolean
  }) => void
  // STEPS.NUMBER_OF_CHILDREN
  onBackNumberOfChildren: () => void
  onCompleteNumberOfChildren: (formValues: { numberOfChildren: string }) => void
  // STEPS.WHERE_ARE_YOU_IN_JOURNEY
  onBackJourneyStage: (journey: string) => void
  onCompleteJourneyStage: ({
    selectedJourneyStage,
    otherJourneyStage
  }: {
    selectedJourneyStage: string | null
    otherJourneyStage: string | null
  }) => void
  // STEPS.PREGNANCY_MEDICAL_RECORDS_COLLECTION
  onCompleteMedicalRecordsCollection: () => void
  // STEPS.PERSONAL_EMAIL_VERIFICATION
  onCompleteVerifyPersonalEmail: () => void
  // STEPS.DUE_DATE
  onBackDueDate: () => void
  onCompleteDueDate: (formValues: { dueDate: string | null }) => void
  // STEPS.DELIVERY_DATE
  onBackDeliveryDate: () => void
  onCompleteDeliveryDate: (formValues: { deliveryDate: string | null }) => void
}

export const useRegistrationNavigation = (registeringUser: RegisteringUser): UseRegistrationNavigation => {
  const { mutate: updateRegistrationMemberActions } = useUpdateRegisteringMemberActionTracking()
  const { trackExperimentEvent } = useTrackExperimentEvent()
  const getIsExcludedFromMedicalRecordsConsent = useGetIsExcludedFromMedicalRecordsConsent()

  const intl = useIntl()
  const { locale } = intl
  const createUserMutation = useCreateUser()

  const { hasSaml, ssoOnly, countryCode, isUsa, companyId, parentCompanyId } = registeringUser || {}
  const isRTXParentCompany = parentCompanyId === RTX_PARENT_COMPANY_ID
  const isSsoOnly = hasSaml && ssoOnly

  const {
    setStep,
    setPersonalEmail,
    preferredName,
    setJourney,
    setSomethingElseJourney,
    setSkippedJourney,
    setJourneyStage,
    setOtherJourneyStage,
    setNumberOfChildren,
    setPassword,
    journey,
    personalEmail,
    setIsSubmitting,
    guid,
    password,
    emailOptIn,
    rtxRewardsOptIn,
    setRtxRewardsOptIn,
    readTerms,
    readPolicy,
    healthConsent,
    dbFormattedPhoneNumber,
    isSubmitting,
    journeyStage,
    otherJourneyStage,
    somethingElseJourney,
    numberOfChildren,
    skippedJourney,
    phoneNumberCountryCode,
    allowsMarketingTexts,
    dueDate,
    setDueDate,
    deliveryDate,
    setDeliveryDate,
    address,
    setAddress,
    setRegistrationErrorEmail
  } = useRegistration()

  const hasIvfRequirements =
    registeringUser?.benefit?.journeys?.fertilityCare?.eligibleExpensesDetail?.ivfRequirementsApply
  const assistedReproductionJourneyStagesWithLikelyKnownClinic: ReadonlySet<string> = new Set([
    "GETTING_READY_START_IVF",
    "ALREADY_STARTED_IVF_CYCLE",
    "RECENTLY_COMPLETED_IVF_CYCLE"
  ])

  // STEPS.INTRO
  const onCompleteIntro = async (): Promise<void> => {
    await trackExperimentEvent("registration-welcome-continue")
    updateRegistrationMemberActions({
      registrationGuid: guid,
      memberActionTaken: MemberActions.REGISTRATION_WELCOME_CONTINUE
    })
    setStep(isSsoOnly ? STEPS.PERSONAL_EMAIL : STEPS.CONFIRM_ACCOUNT_EMAIL)
  }

  // STEPS.CONFIRM_ACCOUNT_EMAIL
  const onBackConfirmAccountEmail = (): void => {
    setStep(STEPS.INTRO)
  }

  const onCompleteConfirmAccountEmail = (): void => {
    setStep(STEPS.SET_PASSWORD)
  }

  // STEPS.SET_PASSWORD
  const onBackSetPassword = (): void => {
    setStep(STEPS.CONFIRM_ACCOUNT_EMAIL)
  }

  const onCompleteSetPassword = (password: string): void => {
    setPassword(password)
    setStep(STEPS.PERSONAL_EMAIL)
  }

  // STEPS.PERSONAL_EMAIL
  const onBackPersonalEmail = (): void => {
    if (isSsoOnly) {
      setStep(STEPS.INTRO)
    } else {
      setStep(STEPS.SET_PASSWORD)
    }
  }

  const onCompletePersonalEmail = (email: string | null): void => {
    setPersonalEmail(email)
    !preferredName ? setStep(STEPS.PREFERRED_NAME) : setStep(STEPS.ADDRESS)
  }
  // STEPS.PREFERRED_NAME
  const onBackPreferredName = (): void => {
    setStep(STEPS.PERSONAL_EMAIL)
  }

  const onCompletePreferredName = (): void => {
    setStep(STEPS.ADDRESS)
  }

  // STEPS.ADDRESS
  const onBackAddress = (): void => {
    setStep(STEPS.PREFERRED_NAME)
  }

  const onCompleteAddress = (address: AboutYou["address"]): void => {
    setAddress(address)
    setStep(STEPS.PHONE_NUMBER_COLLECTION)
  }

  // STEPS.PHONE_NUMBER_COLLECTION
  const onBackPhoneNumberCollection = (): void => {
    setStep(STEPS.ADDRESS)
  }

  const onCompletePhoneNumberCollection = (): void => {
    setStep(STEPS.WHAT_KIND_OF_JOURNEY)
  }

  // STEPS.ACKNOWLEDGE_TERMS
  const onBackAcknowledgeTerms = (): void => {
    if (isPregnancyTrimesterStage(journeyStage)) {
      setStep(STEPS.DUE_DATE)
      return
    }

    if (isPostpartumStage(journeyStage)) {
      setStep(STEPS.DELIVERY_DATE)
      return
    }

    // Otherwise, use existing logic
    const shouldShowJourneyStage = journey && journey !== "SOMETHING_ELSE"
    setStep(shouldShowJourneyStage ? STEPS.WHERE_ARE_YOU_IN_JOURNEY : STEPS.WHAT_KIND_OF_JOURNEY)
  }

  const onCompleteAcknowledgeTerms = (): void => {
    setStep(STEPS.HEALTH_CONSENT)
  }

  // STEPS.HEALTH_CONSENT
  const sendVerificationEmailAndSetStep = async (): Promise<void> => {
    try {
      setIsSubmitting(true)
      await carrotClient.sendPersonalEmailVerificationEmail()
    } catch {
      window.location.href = Paths.HOME
    }
    setStep(STEPS.PERSONAL_EMAIL_VERIFICATION)
  }

  const onBackHealthConsent = (): void => {
    setStep(STEPS.ACKNOWLEDGE_TERMS)
  }

  const onCompleteHealthConsent = (): void => {
    if (isRTXParentCompany) {
      setStep(STEPS.RTX_REWARDS_OPT_IN)
    } else {
      if (getIsDefaultEmailOptIn(countryCode)) {
        handleCreateUser()
      } else {
        setStep(STEPS.EMAIL_OPT_IN)
      }
    }
  }

  // STEPS.RTX_REWARDS_OPT_IN
  const onCompleteRTXRewardsOptIn = (optedIn: boolean): void => {
    setRtxRewardsOptIn(optedIn)
    if (getIsDefaultEmailOptIn(countryCode)) {
      handleCreateUser(optedIn)
    } else {
      setStep(STEPS.EMAIL_OPT_IN)
    }
  }

  const onBackRTXRewardsOptIn = (): void => {
    setStep(STEPS.HEALTH_CONSENT)
  }

  // STEPS.EMAIL_OPT_IN
  const onBackEmailOptIn = (): void => {
    if (isRTXParentCompany) {
      setStep(STEPS.RTX_REWARDS_OPT_IN)
    } else {
      setStep(STEPS.HEALTH_CONSENT)
    }
  }

  const onCompleteEmailOptIn = (): void => {
    handleCreateUser()
  }

  // STEPS.WHAT_KIND_OF_JOURNEY
  const onBackJourney = (): void => {
    setStep(STEPS.PHONE_NUMBER_COLLECTION)
  }

  const clearOutIrrelevantJourneyAnswers = (): void => {
    setJourneyStage(null)
    setOtherJourneyStage(null)
    setNumberOfChildren(null)
    setDueDate(null)
    setDeliveryDate(null)
  }

  const clearOutAllJourneyState = (): void => {
    setJourney(null)
    setSomethingElseJourney(null)
    clearOutIrrelevantJourneyAnswers()
  }

  const onSkipJourney = (): void => {
    clearOutAllJourneyState()
    setSkippedJourney(true)
  }

  const changeJourney = (selectedJourney: string): void => {
    setJourney(selectedJourney)
    setSomethingElseJourney(null)
    clearOutIrrelevantJourneyAnswers()
    setSkippedJourney(false)
  }

  const setSomethingElseJourneyState = (selectedJourney: string, somethingElseJourney: string): void => {
    setJourney(selectedJourney)
    setSomethingElseJourney(somethingElseJourney)
    clearOutIrrelevantJourneyAnswers()
    setSkippedJourney(false)
  }

  const setStepAfterJourney = (journey: string | null): void => {
    if (journey) {
      switch (journey) {
        case "PARENTING":
          setStep(STEPS.NUMBER_OF_CHILDREN)
          break
        case "SOMETHING_ELSE":
          setStep(STEPS.ACKNOWLEDGE_TERMS)
          break
        default:
          setStep(STEPS.WHERE_ARE_YOU_IN_JOURNEY)
      }
    } else {
      setStep(STEPS.ACKNOWLEDGE_TERMS)
    }
  }

  const onCompleteJourney = ({
    selectedJourney,
    somethingElseJourney,
    skipped
  }: {
    selectedJourney: string
    somethingElseJourney: string
    skipped: boolean
  }): void => {
    const journeyChanged = selectedJourney !== journey
    const journeyIsSomethingElse = somethingElseJourney && selectedJourney === "SOMETHING_ELSE"

    if (skipped) {
      onSkipJourney()
    } else if (journeyChanged && !journeyIsSomethingElse) {
      changeJourney(selectedJourney)
    } else if (journeyIsSomethingElse) {
      setSomethingElseJourneyState(selectedJourney, somethingElseJourney)
    }
    setStepAfterJourney(selectedJourney)
  }

  // STEPS.NUMBER_OF_CHILDREN
  const onBackNumberOfChildren = (): void => {
    setStep(STEPS.WHAT_KIND_OF_JOURNEY)
  }

  const onCompleteNumberOfChildren = (formValues: { numberOfChildren: string }): void => {
    setNumberOfChildren(formValues.numberOfChildren)
    setStep(STEPS.WHERE_ARE_YOU_IN_JOURNEY)
  }

  // STEPS.WHERE_ARE_YOU_IN_JOURNEY
  const onBackJourneyStage = (journey: string): void => {
    journey === "PARENTING" ? setStep(STEPS.NUMBER_OF_CHILDREN) : setStep(STEPS.WHAT_KIND_OF_JOURNEY)
  }

  const onCompleteJourneyStage = ({
    selectedJourneyStage,
    otherJourneyStage
  }: {
    selectedJourneyStage: string | null
    otherJourneyStage: string | null
  }): void => {
    if (selectedJourneyStage !== journeyStage || !selectedJourneyStage) {
      setOtherJourneyStage(null)
      setDueDate(null)
      setDeliveryDate(null)
    }

    setJourneyStage(selectedJourneyStage)
    setOtherJourneyStage(otherJourneyStage)

    // Route based on journey stage
    if (isPregnancyTrimesterStage(selectedJourneyStage)) {
      setStep(STEPS.DUE_DATE)
    } else if (isPostpartumStage(selectedJourneyStage)) {
      setStep(STEPS.DELIVERY_DATE)
    } else {
      setStep(STEPS.ACKNOWLEDGE_TERMS)
    }
  }

  // STEPS.DUE_DATE
  const onBackDueDate = (): void => {
    setStep(STEPS.WHERE_ARE_YOU_IN_JOURNEY)
  }

  const onCompleteDueDate = (formValues: { dueDate: string | null }): void => {
    setDueDate(formValues.dueDate)
    setStep(STEPS.ACKNOWLEDGE_TERMS)
  }

  // STEPS.DELIVERY_DATE
  const onBackDeliveryDate = (): void => {
    setStep(STEPS.WHERE_ARE_YOU_IN_JOURNEY)
  }

  const onCompleteDeliveryDate = (formValues: { deliveryDate: string | null }): void => {
    setDeliveryDate(formValues.deliveryDate)
    setStep(STEPS.ACKNOWLEDGE_TERMS)
  }

  // STEPS.PREGNANCY_MEDICAL_RECORDS_COLLECTION
  const onCompleteMedicalRecordsCollection = (): void => {
    setPersonalEmailVerificationOrRegistrationComplete()
  }

  // STEPS.PERSONAL_EMAIL_VERIFICATION
  const onCompleteVerifyPersonalEmail = (): void => {
    completeRegistration()
  }

  // Create the user
  const setPersonalEmailVerificationOrRegistrationComplete = async (): Promise<void> => {
    if (!personalEmail) {
      completeRegistration()
    } else {
      await sendVerificationEmailAndSetStep()
    }
  }

  const completeRegistration = (): void => {
    setStep(STEPS.REGISTRATION_COMPLETED)
  }

  const handleCreateUser = async (rtxRewardsOptInValue?: boolean): Promise<void> => {
    if (isSubmitting) return
    setIsSubmitting(true)

    createUserMutation.mutate(
      {
        guid,
        password,
        emailOptIn,
        rtxRewardsOptIn: rtxRewardsOptInValue ?? rtxRewardsOptIn,
        agreeToTerms: readTerms,
        agreeToPolicy: readPolicy,
        agreeToHealthData: healthConsent,
        personalEmail,
        preferredName,
        registrationJourney: {
          journeyType: journey,
          somethingElseJourney,
          journeyStage,
          otherJourneyStage,
          numberOfChildren,
          skipped: skippedJourney,
          dueDate,
          deliveryDate
        },
        address,
        phoneNumber: dbFormattedPhoneNumber,
        phoneNumberCountryCode,
        allowsMarketingTexts,
        // eslint-disable-next-line no-restricted-syntax -- We include the user's preferredLocale in the request to create their account. Carrot-app's LocaleService will then determine if they can use the selected language once authenticated.
        preferredLocale: locale
      },
      {
        onSuccess: () => {
          if (
            isUsa &&
            journey === "PREGNANT" &&
            !getIsExcludedFromMedicalRecordsConsent({ companyId, parentCompanyId })
          ) {
            setStep(STEPS.PREGNANCY_MEDICAL_RECORDS_COLLECTION)
          } else if (
            isUsa &&
            !hasIvfRequirements &&
            journey === "ASSISTED_REPRODUCTION" &&
            assistedReproductionJourneyStagesWithLikelyKnownClinic.has(journeyStage) &&
            !getIsExcludedFromMedicalRecordsConsent({ companyId, parentCompanyId })
          ) {
            setStep(STEPS.ART_MEDICAL_RECORDS_COLLECTION)
          } else {
            setPersonalEmailVerificationOrRegistrationComplete()
          }
        },
        onError: async (error: unknown) => {
          setIsSubmitting(false)
          if (isHttpError(error)) {
            const body = await parseErrorBody(error)
            if (body?.code === CarrotErrorCodes.REGISTRATION_EMAIL_IN_USE_BY_PARTNER) {
              setRegistrationErrorEmail(body?.email ?? null)
              setStep(STEPS.EMAIL_IN_USE_BY_PARTNER)
              return
            }
            if (body?.code === CarrotErrorCodes.REGISTRATION_EMAIL_ALREADY_IN_USE) {
              setRegistrationErrorEmail(body?.email ?? null)
              setStep(STEPS.EMAIL_ALREADY_IN_USE)
              return
            }
          }
          Helpers.browserRedirect(Paths.HOME)
        }
      }
    )
  }

  return {
    onCompleteIntro,
    onBackConfirmAccountEmail,
    onCompleteConfirmAccountEmail,
    onBackSetPassword,
    onCompleteSetPassword,
    onBackPersonalEmail,
    onCompletePersonalEmail,
    onBackPreferredName,
    onCompletePreferredName,
    onBackAddress,
    onCompleteAddress,
    onBackPhoneNumberCollection,
    onCompletePhoneNumberCollection,
    onBackAcknowledgeTerms,
    onCompleteAcknowledgeTerms,
    onBackHealthConsent,
    onCompleteHealthConsent,
    onBackEmailOptIn,
    onCompleteEmailOptIn,
    onBackRTXRewardsOptIn,
    onCompleteRTXRewardsOptIn,
    onBackJourney,
    onCompleteJourney,
    onBackNumberOfChildren,
    onCompleteNumberOfChildren,
    onBackJourneyStage,
    onCompleteJourneyStage,
    onCompleteMedicalRecordsCollection,
    onCompleteVerifyPersonalEmail,
    onBackDueDate,
    onCompleteDueDate,
    onBackDeliveryDate,
    onCompleteDeliveryDate
  }
}
